import React, { useState, useEffect, Fragment } from "react";
import ProductPricesPicker from "components/ProductPricesPicker";
import { useFormikContext } from "formik";
import get from "lodash/get";
import compact from "lodash/compact";
import cloneDeep from "lodash/cloneDeep";
import { COLORS } from "utils/styles";
import HoverIconButton from "components/HoverIconButton";
import ConfirmIconButton from "components/ConfirmIconButton";
import QuantityControl from "components/QuantityControl";
import FieldError from "components/FieldError";
import SlimCTA from "components/SlimCTA";
import classnames from "classnames";
import { SHORT_LINK_PRODUCT_REMOVAL } from "utils/constants/confirm";
import {
  useAppBuilder,
  useCtxReferences,
  useIsAuthed,
  useProductsPrices
} from "utils/selectors";
import Overlay from "components/Overlay";
import ModelSearchList from "components/ModelSearchList";
import ProductCard from "components/ProductCard";
import ProductCell from "components/ProductCell";
import { orderedBuilderProducts, prepareProductList } from "utils/product";
import { PRODUCT } from "utils/constants/models";
import {
  LIST_PRESENTATION,
  EXTENDED_OVERLAY_CONTENT,
  secondaryBackButton
} from "utils/constants/ui";
import ProductCreate from "components/ProductForm/Create";
import PriceInfo from "./PriceInfo";
import UnauthedPickerPrompt from "components/UnauthedPickerPrompt";
import {
  getSelectedProductPrimary,
  getSelectedPricePrimary
} from "utils/primaries";
import { STATE_KEYS } from "utils/constants/state";
import { BILLING_SCHEME } from "utils/constants/payment";
import { useLocationModelContext } from "utils/hooks/editor";
import { RECURRING_USAGE_TYPE } from "utils/constants/stripe";

const QUANTITY_MIN_HEIGHT = { minHeight: 55 };

const PICK_INTENT = {
  PRODUCT: "product",
  PRICE: "price",
  NEW_PRODUCT: "new_product"
};

const MODEL_SEARCH_CLASSES = {
  container: "h-100",
  inputWrapper: "flex flex-row items-center pb3",
  item: "mv2",
  option: {
    option: "f6"
  }
};

const CheckoutProductPricePicker = ({
  name,
  editMode,
  arrayHelpers,
  modelIx,
  showOnMount,
  unsetMountKey
}) => {
  const modelContext = useLocationModelContext();
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const { resourceUUID, resource } = useCtxReferences();
  const isAuthed = useIsAuthed();
  const builder = useAppBuilder();

  const model = get(values, name);
  const autoTaxes = Boolean(
    get(values, STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED)
  );

  /**
   * Allow the user to toggle between preview and edit mode
   */
  const [showOverlay, setShowOverlay] = useState(showOnMount);
  /**
   * Allow the user to toggle between product and price pick intent
   */
  const [intent, setIntent] = useState(
    model.product ? PICK_INTENT.PRICE : PICK_INTENT.PRODUCT
  );

  /**
   * Form reset can put the user in a state where
   * - product is now unset
   * - but setter mode is price
   * So we need to reset mode as a side effect to this change
   */
  useEffect(() => {
    if (!model.product && intent === PICK_INTENT.PRICE) {
      setIntent(PICK_INTENT.PRODUCT);
    }
  }, [model.product]);

  /**
   * Unset the mount key within the parentmif present
   * - this prevents using an expired mount key when any of the pickers are deleted
   * - i.e. the modal menu shows when a product has been deleted
   */
  useEffect(() => {
    if (showOnMount && unsetMountKey) {
      unsetMountKey();
    }
  }, []);

  const productPath = `${name}.product`;
  const pricePath = `${name}.price`;
  const currencyPath = `${name}.currency`;

  const { products, prices } = useProductsPrices();

  const productMatch = products.find((prod) => prod.id === model.product);
  const priceMatch = prices.find((price) => price.id === model.price);

  const activeModelIds = productMatch ? [productMatch.id] : [];

  let overlayContent = null;
  const pickProduct = intent === PICK_INTENT.PRODUCT && editMode;
  const pickPrice = intent === PICK_INTENT.PRICE && model.product && editMode;

  const newProduct = intent === PICK_INTENT.NEW_PRODUCT && isAuthed && editMode;

  const onSelect = (selection) => {
    if (model.product !== selection) {
      const productPrices = prices.filter(
        (price) => price.product === selection
      );
      /**
       * Eager set when only a single price
       * Check if we can set price and currency immediately
       * Yes - if there is only one product price
       */
      const ctxPrice = productPrices.length === 1 && productPrices[0];
      const current = cloneDeep(get(values, name));

      const updatedPrimary = getSelectedProductPrimary({
        current,
        product: selection,
        priceModel: ctxPrice
      });

      setFieldValue(name, updatedPrimary);
      setIntent(PICK_INTENT.PRICE);

      setTimeout(() => setFieldTouched(productPath, true), 100);
    } else {
      setIntent(PICK_INTENT.PRICE);
    }
  };

  /**
   * Each Picker row enables the User to
   * - Create a new product && select price
   * - Select an existing product && price
   */
  if (newProduct) {
    const buttonProps = secondaryBackButton({
      onClick: () => setIntent(PICK_INTENT.PRODUCT),
      tooltip: "All products"
    });
    const createProps = {
      header: (
        <div className="flex flex-row items-center">
          <HoverIconButton {...buttonProps} />
        </div>
      ),
      /**
       * Override default afterCreate handler to allow for product selection
       * - instead of default navigation (i.e. go back / go show)
       * @param {Error} err
       * @param {Object<{products: Array}>} response
       */
      afterCreate: (err, response) => {
        if (!err) {
          const newProdId = get(response, "products[0].id");
          if (newProdId) {
            setTimeout(() => onSelect(newProdId), 0);
          } else {
            throw new Error("No product id for after create");
          }
        }
      }
    };

    overlayContent = <ProductCreate {...createProps} />;
  } else if (pickProduct) {
    let preparedProducts = prepareProductList({
      products,
      prices
    });

    /**
     * Enfore order of products when not authed
     */
    if (!isAuthed && builder.productPrices && builder.productPrices.length) {
      preparedProducts = orderedBuilderProducts({
        builder,
        products: preparedProducts
      });
    }
    let newActions;
    let emptyComponent;
    if (isAuthed && editMode) {
      newActions = [
        {
          theme: "slim",
          copy: "New",
          loading: false,
          onClick: () => setIntent(PICK_INTENT.NEW_PRODUCT)
        }
      ];
      emptyComponent = (
        <SlimCTA
          header="No products"
          subheader="Create a new product to get started"
          actions={newActions}
        />
      );
    }

    overlayContent = (
      <ModelSearchList
        models={preparedProducts || []}
        query={{
          model: PRODUCT,
          keys: ["name", "id"]
        }}
        customClasses={{
          [LIST_PRESENTATION.GRID]: {
            ...MODEL_SEARCH_CLASSES,
            list: {
              list: "grid-container-2 ma0 pa0 list",
              item: "w-100"
            }
          },
          [LIST_PRESENTATION.LIST]: { ...MODEL_SEARCH_CLASSES }
        }}
        components={{
          [LIST_PRESENTATION.GRID]: ProductCell,
          [LIST_PRESENTATION.LIST]: ProductCard
        }}
        /**
         * Allow User to create a new Product when
         * - Authenticated
         * - Edit mode: which is not allowed after creation of the Payment link
         */
        barActions={newActions}
        emptyComponent={emptyComponent}
        onClick={onSelect}
        activeModelIds={activeModelIds}
        insert={
          !isAuthed ? (
            <UnauthedPickerPrompt
              icon="magic"
              header="Test products"
              subheader={`Select one of these test products for your ${modelContext}.`}
            />
          ) : null
        }
      />
    );
  } else if (pickPrice) {
    overlayContent = (
      <div className="pa3 pt4 w-100">
        <ProductPricesPicker
          uid={model.product}
          resourceUUID={resourceUUID}
          resource={resource}
          activePrices={compact([model.price])}
          goBack={() => {
            setIntent(PICK_INTENT.PRODUCT);
          }}
          onSelect={(selection) => {
            setShowOverlay(false);
            /**
             * Set both the price, currency and interval for the primary
             */
            const priceModel = prices.find((price) => price.id === selection);
            const current = cloneDeep(get(values, name));

            const updatedPrimary = getSelectedPricePrimary({
              current,
              price: selection,
              priceModel
            });
            setFieldValue(name, updatedPrimary);

            setTimeout(() => setFieldTouched(pricePath, true), 100);
          }}
        />
      </div>
    );
  }

  let quantityInput = null;
  if (priceMatch) {
    const usageType = priceMatch.recurring && priceMatch.recurring.usage_type;
    const perUnitScheme = priceMatch.billing_scheme === BILLING_SCHEME.PER_UNIT;
    const tieredNotMetered =
      priceMatch.billing_scheme === BILLING_SCHEME.TIERED &&
      usageType !== RECURRING_USAGE_TYPE.METERED;
    if (perUnitScheme || tieredNotMetered) {
      quantityInput = <QuantityControl disabled={!editMode} name={name} />;
    }
  }

  const activePriceInfo = priceMatch ? (
    <PriceInfo price={priceMatch} model={model} autoTaxes={autoTaxes} />
  ) : model.product ? (
    <div className="fw5 f6 pr2 hairline-1 pt2 color-primary">
      No price selected
    </div>
  ) : null;
  const overlayHeader =
    pickProduct || pickPrice ? (
      <div className="f3 fw6 ph3 pb2">
        {`Select a ${pickProduct ? "product" : "price"}`}
      </div>
    ) : null;

  return (
    <Fragment>
      <div className="w-100 flex flex-column ba br2 hairline-1">
        <div className="flex flex-column flex-row-ns justify-between items-center-ns">
          <div
            className={classnames(
              "flex flex-row justify-between items-center w-100 pt3 pt0-ns",
              {
                "bg-white--hover": editMode
              }
            )}
          >
            {/* Left side stack */}
            <div
              className={classnames("flex flex-column w-100 ph3 pt0 pt3-ns", {
                "pointer": editMode,
                "pb3-ns": !priceMatch || !quantityInput
              })}
              onClick={() => editMode && setShowOverlay(!showOverlay)}
            >
              <div
                className={classnames("fw5 f5 pr2 lh-title", {
                  "color-primary": !productMatch,
                  "fw6": productMatch
                })}
              >
                {productMatch ? productMatch.name : "Select a product"}
              </div>
              {/* Large display price info */}
              <div className="dn flex-ns flex-row items-center">
                {activePriceInfo}
              </div>
            </div>
            {/* Controls */}
            <div className="pr3 flex flex-column items-end">
              {editMode && (
                <div className="flex flex-row items-end">
                  <div className="ph2">
                    <ConfirmIconButton
                      slides={SHORT_LINK_PRODUCT_REMOVAL}
                      onConfirm={() => {
                        arrayHelpers.remove(modelIx);
                        if (!get(touched, name)) {
                          setFieldTouched(name, true);
                        }
                      }}
                    />
                  </div>
                  <HoverIconButton
                    onClick={() => editMode && setShowOverlay(!showOverlay)}
                    tooltip={!showOverlay && "Show details"}
                    color={showOverlay ? COLORS.blue : COLORS.primary}
                    type={showOverlay ? "eye" : "eyeClosed"}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Small - Medium display price info */}
          <div
            className={classnames(
              "flex dn-ns flex-column justify-between items-center ph3 pb3",
              {
                "bb hairline-1": showOverlay && pickPrice
              }
            )}
          >
            <div
              className="flex flex-row justify-between w-100"
              onClick={() => editMode && setShowOverlay(!showOverlay)}
            >
              {activePriceInfo}
            </div>
            {priceMatch && quantityInput && (
              <div className="flex w-100" style={QUANTITY_MIN_HEIGHT}>
                {quantityInput}
              </div>
            )}
          </div>
        </div>
        {showOverlay && overlayContent && (
          <Overlay
            hideOverlay={() => setShowOverlay(false)}
            customClasses={{ content: EXTENDED_OVERLAY_CONTENT }}
          >
            <div className="pt3">
              {overlayHeader}
              {overlayContent}
            </div>
          </Overlay>
        )}
        {priceMatch && quantityInput && (
          <div
            style={QUANTITY_MIN_HEIGHT}
            className="dn flex-ns flex-row items-center justify-end ph3 pb3 pt1"
          >
            {quantityInput}
          </div>
        )}
      </div>
      <FieldError name={productPath} />
      <FieldError name={pricePath} />
    </Fragment>
  );
};

export default CheckoutProductPricePicker;
